import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { FileUpload } from "./FileUpload";
import { useAutoSave } from "@/hooks/useAutoSave";
import { AutoSaveIndicator } from "./AutoSaveIndicator";

export const ProfileForm = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    full_name: "",
    location: "",
    email: "",
    phone_number: "",
    linkedin_url: "",
    website_url: "",
    github_url: "",
    instagram_url: "",
    facebook_url: "",
    facebook_page_url: "",
    twitter_url: "",
    dribbble_url: "",
    behance_url: "",
    medium_url: "",
    discord_username: "",
    slack_url: "",
    whatsapp_number: "",
    telegram_username: "",
    youtube_url: "",
    tiktok_url: "",
    pinterest_url: "",
    resume_pdf_url: "",
    profile_photo_url: "",
    summary: "",
  });

  const { data: profile } = useQuery({
    queryKey: ["admin-profile"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profile")
        .select("*")
        .single();
      if (error && error.code !== "PGRST116") throw error;
      return data;
    },
  });

  const { status, lastSaved } = useAutoSave({
    entityType: "profile",
    entityId: profile?.id,
    data: formData,
    enabled: !!profile?.id,
  });

  useEffect(() => {
    if (profile) {
      setFormData(profile);
    }
  }, [profile]);

  const mutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (profile?.id) {
        const { error } = await supabase
          .from("profile")
          .update(data)
          .eq("id", profile.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("profile").insert([data]);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-profile"] });
      queryClient.invalidateQueries({ queryKey: ["profile"] });
      toast.success("Profile updated successfully");
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-foreground">Profile Information</h2>
        <AutoSaveIndicator status={status} lastSaved={lastSaved} />
      </div>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="full_name">Full Name *</Label>
          <Input
            id="full_name"
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="location">Location</Label>
          <Input
            id="location"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="linkedin_url">LinkedIn URL</Label>
          <Input
            id="linkedin_url"
            type="url"
            value={formData.linkedin_url}
            onChange={(e) => setFormData({ ...formData, linkedin_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="website_url">Website URL</Label>
          <Input
            id="website_url"
            type="url"
            value={formData.website_url}
            onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="github_url">GitHub URL</Label>
          <Input
            id="github_url"
            type="url"
            value={formData.github_url}
            onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="instagram_url">Instagram URL</Label>
          <Input
            id="instagram_url"
            type="url"
            value={formData.instagram_url}
            onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="facebook_url">Facebook Profile URL</Label>
          <Input
            id="facebook_url"
            type="url"
            value={formData.facebook_url}
            onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="facebook_page_url">Facebook Page URL</Label>
          <Input
            id="facebook_page_url"
            type="url"
            value={formData.facebook_page_url}
            onChange={(e) => setFormData({ ...formData, facebook_page_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="twitter_url">X (Twitter) URL</Label>
          <Input
            id="twitter_url"
            type="url"
            value={formData.twitter_url}
            onChange={(e) => setFormData({ ...formData, twitter_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="dribbble_url">Dribbble URL</Label>
          <Input
            id="dribbble_url"
            type="url"
            value={formData.dribbble_url}
            onChange={(e) => setFormData({ ...formData, dribbble_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="behance_url">Behance URL</Label>
          <Input
            id="behance_url"
            type="url"
            value={formData.behance_url}
            onChange={(e) => setFormData({ ...formData, behance_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="medium_url">Medium URL</Label>
          <Input
            id="medium_url"
            type="url"
            value={formData.medium_url}
            onChange={(e) => setFormData({ ...formData, medium_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="youtube_url">YouTube URL</Label>
          <Input
            id="youtube_url"
            type="url"
            value={formData.youtube_url}
            onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tiktok_url">TikTok URL</Label>
          <Input
            id="tiktok_url"
            type="url"
            value={formData.tiktok_url}
            onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="pinterest_url">Pinterest URL</Label>
          <Input
            id="pinterest_url"
            type="url"
            value={formData.pinterest_url}
            onChange={(e) => setFormData({ ...formData, pinterest_url: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone_number">Phone Number</Label>
          <Input
            id="phone_number"
            type="tel"
            value={formData.phone_number}
            onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
            placeholder="+1 (555) 123-4567"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="whatsapp_number">WhatsApp Number</Label>
          <Input
            id="whatsapp_number"
            type="tel"
            value={formData.whatsapp_number}
            onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
            placeholder="+1 (555) 123-4567"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="telegram_username">Telegram Username</Label>
          <Input
            id="telegram_username"
            value={formData.telegram_username}
            onChange={(e) => setFormData({ ...formData, telegram_username: e.target.value })}
            placeholder="@username"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="discord_username">Discord Username</Label>
          <Input
            id="discord_username"
            value={formData.discord_username}
            onChange={(e) => setFormData({ ...formData, discord_username: e.target.value })}
            placeholder="username#1234"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="slack_url">Slack Profile URL</Label>
          <Input
            id="slack_url"
            type="url"
            value={formData.slack_url}
            onChange={(e) => setFormData({ ...formData, slack_url: e.target.value })}
          />
        </div>

        <FileUpload
          bucket="profile-photos"
          label="Profile Photo"
          accept="image/*"
          currentUrl={formData.profile_photo_url}
          onUploadComplete={(url) => setFormData({ ...formData, profile_photo_url: url })}
        />

        <FileUpload
          bucket="documents"
          label="Resume PDF"
          accept="application/pdf"
          currentUrl={formData.resume_pdf_url}
          onUploadComplete={(url) => setFormData({ ...formData, resume_pdf_url: url })}
        />

        <div className="space-y-2">
          <Label htmlFor="summary">Summary</Label>
          <Textarea
            id="summary"
            value={formData.summary}
            onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
            rows={6}
            className="bg-background"
          />
        </div>

        <Button type="submit" disabled={mutation.isPending} className="w-full">
          {mutation.isPending ? "Saving..." : "Save Profile"}
        </Button>
      </form>
    </Card>
  );
};
